'use client';

import type { FirebaseError } from 'firebase/app';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';

import { signOut as signOutService } from '@/services/firebaseServices/AuthServices/authentication';
import type { User } from '@/types/auth';
import { isUserAuthenticated } from '@/utils/auth-helpers';
import { auth, deleteJWTCookie, setJWTCookie } from '@/utils/firebase-helpers';

/**
 * Official Firebase auth pattern with JWT priority checking
 * Checks authentication in priority order:
 * 1. JWT cookie (primary)
 * 2. Firebase auth state (secondary)
 */
export default function useFirebaseAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;
    let unsubscribeToken: (() => void) | undefined;

    const setupAuthListener = async () => {
      try {
        const authInstance = await auth();

        // Official Firebase pattern: onAuthStateChanged for user state
        unsubscribeAuth = onAuthStateChanged(authInstance, (firebaseUser) => {
          if (firebaseUser !== null) {
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
            });
          } else {
            setUser(null);
            deleteJWTCookie();
          }
          setLoading(false);
        });

        // Token refresh: immediately update JWT cookie and spaceId when Firebase token changes
        unsubscribeToken = onIdTokenChanged(authInstance, async (firebaseUser) => {
          if (firebaseUser !== null) {
            // Force token refresh and immediately update cookie
            const token = await firebaseUser.getIdToken(true);
            await setJWTCookie(token);
          }
        });
      } catch (error) {
        setUser(null);
        setLoading(false);
        // Let unexpected errors propagate for proper error tracking
        if ((error as FirebaseError).code?.startsWith('auth/')) {
          // Expected Firebase auth initialization errors are handled silently
          return;
        }
        throw error;
      }
    };

    setupAuthListener();

    return () => {
      unsubscribeAuth?.();
      unsubscribeToken?.();
    };
  }, []);

  const signOut = useCallback(async () => {
    await signOutService();
  }, []);

  return {
    user,
    loading,
    signOut,
    isAuthenticated: isUserAuthenticated(user),
  };
}
